/**
 * As pessoas da família, em tabela.
 *
 * Papel: mostra uma linha por pessoa, com o nome completo, o apelido e os
 * botões de editar e remover.
 *
 * Como usar: quem monta a tabela passa `onEditar` e `onRemover` apenas para
 * quem tem nível `editar`; sem eles a coluna de ações some inteira.
 *
 * O nome aparece por extenso, e não abreviado pelo apelido: dois membros da
 * família compartilham o primeiro nome, e é o nome inteiro que distingue quem é
 * quem ao escolher a pessoa de um documento.
 */
import { type PessoaFamilia } from '@concierge/shared';

import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

interface TabelaPessoasFamiliaProps {
  pessoas: PessoaFamilia[];
  /** Chamado com a pessoa da linha; ausente esconde a coluna de ações. */
  onEditar?: (pessoa: PessoaFamilia) => void;
  /** Chamado com a pessoa a excluir; a confirmação é de quem monta a tabela. */
  onRemover?: (pessoa: PessoaFamilia) => void;
}

/**
 * A tabela de pessoas da família.
 *
 * @param pessoas - As pessoas a mostrar, já em ordem.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @returns A tabela com uma linha por pessoa.
 */
export function TabelaPessoasFamilia({
  pessoas,
  onEditar,
  onRemover,
}: TabelaPessoasFamiliaProps) {
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Nome completo</TableHead>
          <TableHead>Apelido</TableHead>
          {mostrarAcoes && (
            <TableHead>
              <span className="sr-only">Ações</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {pessoas.map((pessoa) => (
          <TableRow key={pessoa.id}>
            <TableCell className="text-texto-medio">{pessoa.nomeCompleto}</TableCell>
            <TableCell>{pessoa.apelido}</TableCell>
            {mostrarAcoes && (
              <TableCell>
                <div className="flex gap-2">
                  {onEditar !== undefined && (
                    <Button variant="outline" size="sm" onClick={() => onEditar(pessoa)}>
                      editar
                    </Button>
                  )}
                  {onRemover !== undefined && (
                    <Button variant="destructive" onClick={() => onRemover(pessoa)}>
                      remover
                    </Button>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
